import { conversationConstants } from './constants';

// Polymorphic `record_type` values the BE sends on a conversation
// (`_base_message.json.jbuilder` → `json.type message.record_type`). These three
// are the ones that pair with the `kula_platform` channel — the candidate's
// "Contact recruiter" message, which can come from any of three candidate-facing
// screens (see `CandidateMessage#push_notification_for_kula_platform`).
// `atsAssessmentRecordType` lives in ./utils and is used for the row actions.
export const ATS_SCHEDULE_RECORD_TYPE = 'AtsSchedule';
export const ATS_CANDIDATE_AVAILABILITY_RECORD_TYPE =
  'AtsCandidateAvailability';
export const CANDIDATE_CONSENT_RECORD_TYPE = 'CandidateConsent';

// What the candidate was looking at when they sent the message. All three
// screens report the same `kula_platform` channel, so the record type is the
// only thing that tells them apart — before this, every one of them read
// "slot booking screen".
//
// `hasName: false` for consent because the BE sends no `schedule` object for it
// (the jbuilder only builds one for AtsSchedule / AtsCandidateAvailability).
const KULA_PLATFORM_SCREENS: Record<
  string,
  { screen: string; hasName: boolean }
> = {
  [ATS_SCHEDULE_RECORD_TYPE]: {
    screen: 'slot booking screen',
    hasName: true,
  },
  [ATS_CANDIDATE_AVAILABILITY_RECORD_TYPE]: {
    screen: 'availability request screen',
    hasName: true,
  },
  [CANDIDATE_CONSENT_RECORD_TYPE]: { screen: 'consent screen', hasName: false },
};

type StatusTextConversation = {
  status?: string | null;
  channel?: string | null;
  record?: { type?: string | null } | null;
  // Named after the BE key. For a schedule this is the interview name; for a
  // candidate availability it is the STAGE name.
  schedule?: { name?: string | null } | null;
};

// " through the <name> <screen>" for a candidate message sent from one of the
// Kula-hosted screens, or '' when the channel/record doesn't identify one.
//
// The name is optional on purpose: an availability whose stage is gone (and
// every consent message) has none, and interpolating it blindly used to render
// the literal string "undefined" to the user.
const getPlatformText = (conversation: StatusTextConversation): string => {
  if (conversation?.channel !== conversationConstants.kula_platform.id) {
    return '';
  }
  const recordType = conversation?.record?.type;
  const match = recordType ? KULA_PLATFORM_SCREENS[recordType] : undefined;
  // An unrecognised record type gets no clause at all — better a shorter
  // sentence than one naming the wrong screen.
  if (!match) return '';

  const name = match.hasName ? conversation?.schedule?.name : undefined;
  return name
    ? ` through the ${name} ${match.screen}`
    : ` through the ${match.screen}`;
};

// The "<sender> …to <recipient>" middle clause of a conversation row's header.
export const getMessageStatusText = (
  conversation: StatusTextConversation
): string => {
  if (
    conversation?.status === 'scheduled' &&
    conversation?.channel === conversationConstants.email.id
  ) {
    return 'scheduled to send to';
  }

  const channelText =
    conversationConstants[
      conversation?.channel as keyof typeof conversationConstants
    ]?.value || '';
  return `sent ${channelText}${getPlatformText(conversation)} to`;
};
